/*
 * ESP model page (sections/esp-model-page.liquid, 30 Sep 2026).
 * - <esp-model-page>: tabs (compatible parts, schedule, manuals, FAQ), "Read more", links that open a tab (#parts,
 *   #schedule, #manuals, #faq).
 * - <esp-schedule>: "Customer picks one" lists and the "Complete set" box.
 * Adding to the cart goes through the theme's own <product-cart-form> (theme.js), so the cart drawer, errors and
 * vendor-store properties behave as they do on the product cards.
 * Written for older iPhones too (iOS 13+): no optional chaining or replaceChildren.
 * The set follows the Hydrogen site's ReplacementSchedule rules (ESP Theme/esp-hydrogen), keyed by variant.
 */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class EspModelPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.tablist = this.querySelector('[data-tablist]');
      this.tabs = this.tablist ? [...this.tablist.querySelectorAll('[role="tab"]')] : [];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tabbed = this.tabs.length > 1;
      if (this.tabbed) this.setupTabs();
      this.setupMore();
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-open-tab]');
        if (!link || !this.contains(link)) return;
        const id = link.dataset.openTab;
        if (!this.panel(id)) return;
        event.preventDefault();
        this.open(id, {scroll: true, focus: true});
      });
    }

    panel(id) {
      return this.panels.find((p) => p.dataset.panel === id) || null;
    }

    setupTabs() {
      this.first = this.tabs[0].dataset.tab;
      this.tabs.forEach((tab) => {
        const panel = this.panel(tab.dataset.tab);
        if (!panel) return;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = -1;
        tab.addEventListener('click', () => this.open(tab.dataset.tab));
        tab.addEventListener('keydown', (event) => this.onKey(event, tab));
      });
      this.tablist.hidden = false;
      this.classList.add('is-tabbed');
      const fromHash = () => {
        let id = '';
        try {
          id = decodeURIComponent(window.location.hash.slice(1));
        } catch (error) {
          return null;
        }
        return this.panel(id) ? id : null;
      };
      const initial = fromHash();
      this.open(initial || this.first, {scroll: !!initial, keepHash: true});
      window.addEventListener('hashchange', () => {
        const id = fromHash();
        if (id) this.open(id, {scroll: true, keepHash: true});
      });
    }

    onKey(event, tab) {
      const i = this.tabs.indexOf(tab);
      let next = null;
      if (event.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
      else if (event.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      else if (event.key === 'Home') next = this.tabs[0];
      else if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      this.open(next.dataset.tab);
      next.focus();
    }

    /** Shows a tab's panel (or, without tabs, just goes to it). */
    open(id, {scroll = false, focus = false, keepHash = false} = {}) {
      const panel = this.panel(id);
      if (!panel) return;
      if (this.tabbed) {
        this.tabs.forEach((tab) => {
          const on = tab.dataset.tab === id;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
        });
        this.panels.forEach((p) => {
          if (p.getAttribute('role') === 'tabpanel') p.hidden = p !== panel;
        });
        if (!keepHash) {
          const url = id === this.first ? window.location.pathname + window.location.search : `#${id}`;
          window.history.replaceState(window.history.state, '', url);
        }
      }
      if (scroll) {
        const target = this.tabbed ? this.querySelector('.esp-model__tabs-bar') || panel : panel;
        target.scrollIntoView({behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start'});
      }
      if (focus) panel.focus({preventScroll: true});
    }

    /** Long descriptions show three lines and "Read more" (the full text is always in the page). */
    setupMore() {
      const desc = this.querySelector('[data-desc][data-long]');
      const button = this.querySelector('[data-more]');
      if (!desc || !button) return;
      desc.classList.add('is-clamped');
      const check = () => {
        if (button.getAttribute('aria-expanded') === 'true') return;
        button.hidden = desc.scrollHeight <= desc.clientHeight + 2;
      };
      check();
      window.addEventListener('resize', check, {passive: true});
      window.addEventListener('load', check);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(check);
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        desc.classList.toggle('is-clamped', !open);
        button.textContent = open ? 'Show less' : 'Read more';
      });
    }
  }

  /* ------------------------------------------------------------------ schedule: picks and the set */

  /** ESP's own kit part numbers: YSM-… holds every filter and the membrane; YS-… every filter but the membrane. */
  const kitPart = (sku) => (/^YSM-/i.test(sku || '') ? 'all' : /^YS-/i.test(sku || '') ? 'no-membrane' : null);
  /** "Sediment filter" → "sediment filter" mid-sentence; "RO membrane" and "UV lamp" keep their capitals. */
  const midSentence = (s) => (/^[A-Z][a-z]/.test(s) ? s[0].toLowerCase() + s.slice(1) : s);
  const joinAnd = (list) => (list.length < 2 ? list.join('') : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`);

  class EspSchedule extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.name = this.dataset.name || '';
      this.kind = this.dataset.kind || 'ro';
      this.customerId = this.dataset.cid || '';
      this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
      this.rowEls = [...this.querySelectorAll('.esp-sched__row')];
      this.rows = this.rowEls.map((el, i) => {
        let data = {};
        try {
          data = JSON.parse(el.dataset.row || '{}');
        } catch (error) {
          data = {};
        }
        return {...data, i, el};
      });
      this.picked = {};
      this.querySelectorAll('[data-pick]').forEach((select) => {
        select.addEventListener('change', () => this.pick(select));
        // A choice the browser restored (Back) counts as picked.
        if (select.value !== '') this.pick(select, false);
      });
      this.render();
    }

    pick(select, rerender = true) {
      const row = select.closest('.esp-sched__row');
      const i = this.rowEls.indexOf(row);
      const k = select.value === '' ? null : Number(select.value);
      this.picked[i] = k;
      row.querySelectorAll(':scope > .esp-sched__choice').forEach((cell) => {
        cell.hidden = k === null ? cell.dataset.choice !== 'none' : cell.dataset.choice !== String(k);
      });
      if (rerender) this.render();
    }

    money(cents) {
      const value = (Number(cents) || 0) / 100;
      const amount = (decimals, thousands = ',', point = '.') => {
        const [whole, fraction] = value.toFixed(decimals).split('.');
        return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? point + fraction : '');
      };
      return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        if (key === 'amount_no_decimals') return amount(0);
        if (key === 'amount_with_comma_separator') return amount(2, '.', ',');
        if (key === 'amount_no_decimals_with_comma_separator') return amount(0, '.', ',');
        if (key === 'amount_with_apostrophe_separator') return amount(2, "'", '.');
        return amount(2);
      });
    }

    /** The product option a row stands for now: its own, or the one the customer picked. */
    cardOf(row) {
      if (Array.isArray(row.choices)) {
        const k = this.picked[row.i];
        return k == null ? null : row.choices[k] || null;
      }
      return row.card || null;
    }

    render() {
      const box = this.querySelector('[data-set]');
      if (!box) return;
      const rows = this.rows.map((r) => {
        const alt = r.kind === 'alt';
        const bundle = r.kind === 'kit';
        return {...r, card: this.cardOf(r), alt, bundle, optional: !!r.optional && !alt && !bundle, qty: Math.max(1, Number(r.qty) || 1)};
      });
      const addable = (card) => !!(card && card.addable);
      // A system with no membrane (a gravity or whole-house filter): its kit holds only filters (LIVE-50).
      const membrane = this.dataset.membrane !== '0';
      const main = (r) => !r.alt && !r.bundle && !r.optional;

      // The full set: every part that isn't an alternative, a kit or "only some versions"; quantities add up.
      // A part whose own SKU is one of ESP's kits (YS-/YSM-) is bought once, however many stages it serves.
      const set = new Map();
      for (const r of rows) {
        if (!main(r) || !addable(r.card)) continue;
        const prev = set.get(r.card.vid);
        const had = prev ? prev.qty : 0;
        set.set(r.card.vid, {card: r.card, qty: kitPart(r.card.sku) ? 1 : had + r.qty});
      }
      const total = (map) => [...map.values()].reduce((sum, x) => sum + x.qty * x.card.price, 0);
      const setCount = [...set.values()].reduce((sum, x) => sum + x.qty, 0);
      const setTotal = total(set);

      const servedBy = (k) => {
        const r = rows.find((x) => main(x) && x.card && kitPart(x.card.sku) === k && set.has(x.card.vid));
        return r ? r.card : null;
      };
      const allKit = servedBy('all');
      const filtersKit = servedBy('no-membrane');

      // Labelled kits (ESP System Part) replace what their label says; the set uses the one that saves most.
      const setRows = rows.filter((r) => main(r) && r.card && set.has(r.card.vid));
      const lampSleeve = rows.filter((r) => main(r) && /\blamp\b|sleeve/i.test(r.label || '') && r.card);
      const plans = rows
        .filter((r) => r.bundle && addable(r.card))
        .map((k) => {
          const filterKit = k.kit === 'all' || k.kit === 'no-membrane';
          const parts = filterKit ? setRows.filter((r) => k.kit === 'all' || !/membrane/i.test(r.label || '')) : lampSleeve;
          const ids = [...new Set(parts.map((r) => r.card.vid))];
          const fits = filterKit
            ? ids.length >= 2
            : parts.some((r) => /\blamp\b/i.test(r.label)) && parts.some((r) => /sleeve/i.test(r.label)) && parts.every((r) => (set.get(r.card.vid) || {}).qty === 1);
          const partsPrice = filterKit ? ids.reduce((sum, id) => sum + set.get(id).qty * set.get(id).card.price, 0) : parts.reduce((sum, r) => sum + r.card.price, 0);
          return {kit: k, ids, fits, filterKit, saving: fits ? partsPrice - k.card.price : 0};
        });
      const best = plans.filter((p) => p.fits && p.saving > 0).sort((a, b) => b.saving - a.saving)[0] || null;
      const plan = best || plans[0] || null;
      const kitSet = new Map(set);
      if (best) {
        best.ids.forEach((id) => kitSet.delete(id));
        kitSet.set(best.kit.card.vid, {card: best.kit.card, qty: 1});
      }

      // Parts the set can't include are named, never silently dropped; unpicked choices ask for the choice.
      const toChoose = rows.filter((r) => Array.isArray(r.choices) && !r.card && !r.optional);
      const leftOut = rows.filter((r) => main(r) && !r.notSold && !(r.card && set.has(r.card.vid)) && !toChoose.includes(r));

      const show = !!allKit || setCount > 1;
      box.hidden = !show;
      if (!show) return;

      const noun = (n) => (this.kind === 'uv' ? (n === 1 ? 'part' : 'parts') : n === 1 ? 'filter' : 'filters');
      const text = (selector, value) => {
        const el = box.querySelector(selector);
        if (!el) return;
        el.hidden = !value;
        if (el.matches('[data-set-left]')) el.querySelector('[data-set-left-text]').textContent = value || '';
        else el.textContent = value || '';
      };
      const choose = toChoose.length ? `Choose your ${joinAnd(toChoose.map((r) => midSentence(r.label || 'part')))} to complete the set.` : '';
      const left = leftOut.length ? `Not in the set: ${leftOut.map((r) => r.label).join(', ')}.` : '';
      const altForm = box.querySelector('[data-set-alt]');
      const mainForm = box.querySelector('[data-set-main]');
      const partsLink = box.querySelector('[data-set-left] a');
      const pageEl = this.closest('esp-model-page');
      if (partsLink) partsLink.hidden = !(pageEl && pageEl.querySelector('[data-panel="parts"]'));

      let meta;
      let note = '';
      let alt = null;
      let mainItems;
      let mainLabel;
      if (allKit) {
        meta = `Every filter and the membrane in one kit, part ${allKit.sku}`;
        if (filtersKit) alt = {items: [{card: filtersKit, qty: 1}], label: `Or just the filters, part ${filtersKit.sku} · ${this.money(filtersKit.price)}`};
        mainItems = [{card: allKit, qty: 1}];
        mainLabel = `Add the kit · ${this.money(allKit.price)}`;
      } else {
        if (best) {
          const onlyKit = kitSet.size === 1;
          const what = best.filterKit ? (onlyKit ? (membrane ? 'Every filter and the membrane' : 'Every filter') : 'The filters') : onlyKit ? 'The lamp and sleeve' : 'Lamp and sleeve';
          meta = `${what} in one kit, part ${best.kit.card.sku}, saving ${this.money(best.saving)}`;
          alt = {items: [...set.values()], label: `Or buy the parts separately · ${this.money(setTotal)}`};
          mainItems = [...kitSet.values()];
          mainLabel = `${onlyKit ? 'Add the kit' : 'Add the set'} · ${this.money(total(kitSet))}`;
        } else {
          meta = `${setCount} ${noun(setCount)}, matched to this system`;
          if (plan) {
            const how = plan.filterKit ? `Or replace them in one box with kit ${plan.kit.card.sku}` : `Or replace the lamp and sleeve together with kit ${plan.kit.card.sku}`;
            note = `${how}${plan.saving > 0 ? `, saving ${this.money(plan.saving)}` : ''}.`;
          }
          mainItems = [...set.values()];
          mainLabel = `Add the set · ${this.money(setTotal)}`;
        }
      }
      text('[data-set-meta]', meta);
      text('[data-set-choose]', choose);
      text('[data-set-left]', left);
      text('[data-set-note]', note);
      this.fill(mainForm, mainItems, mainLabel);
      if (altForm) {
        altForm.hidden = !alt;
        if (alt) this.fill(altForm, alt.items, alt.label);
      }
    }

    /** Puts the items into a <product-cart-form> (Shopify's /cart/add takes items[n][id] and items[n][quantity]). */
    fill(cartForm, items, label) {
      if (!cartForm) return;
      const holder = cartForm.querySelector('[data-items]');
      const inputs = [];
      // Each line carries the same properties a single add sends: the customer id, and the vendor-store tag the theme
      // adds for utm_source visitors (theme.js productCartForm puts it at the top level, which a multi-item add ignores).
      // Brand stores only (window.espStoreUtmSource, layout/theme.liquid): a campaign tag such as Klaviyo is not a store.
      const utm = window.espStoreUtmSource ? window.espStoreUtmSource() : null;
      const store = utm ? `${String(utm).charAt(0).toUpperCase()}${String(utm).slice(1)}Store` : null;
      items.forEach((item, k) => {
        const fields = [['id', item.card.vid], ['quantity', item.qty], ['properties][_intuitive_cid', this.customerId]];
        if (store) fields.push([`properties][${store}`, '']);
        for (const [key, value] of fields) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `items[${k}][${key}]`;
          input.value = String(value);
          inputs.push(input);
        }
      });
      holder.textContent = '';
      inputs.forEach((input) => holder.appendChild(input));
      const button = cartForm.querySelector('button[type="submit"]');
      if (button && button.getAttribute('aria-busy') !== 'true') button.textContent = label;
    }
  }

  if (!customElements.get('esp-model-page')) customElements.define('esp-model-page', EspModelPage);
  if (!customElements.get('esp-schedule')) customElements.define('esp-schedule', EspSchedule);
})();
